import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, Calculator, ChevronDown, ChevronUp, BookOpen, Target, Users, DollarSign, TrendingUp } from 'lucide-react';

export default function SFRCalculatorBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/sfr-calculator')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-sfr-calculator"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to SFR Calculator
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <Calculator className="h-5 w-5 sm:h-7 sm:w-7" />
              Benefits of the SFR Calculator
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            <WhatsNewSection highlight="multi-species" />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of SFR Calculator</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    The SFR (Specific Feed Rate) Calculator provides a precise, biologically grounded method for determining how much feed should be delivered each day based on fish weight, temperature, and biomass. By using a validated SFR reference model rather than fixed feeding tables or intuition, it ensures that feed allocation is aligned with the fish's true physiological demand.
                  </p>
                </section>

                {/* Benefit 1: Improved Feeding Accuracy */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-full flex-shrink-0">
                      <Target className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-2">Improved Feeding Accuracy</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        A key benefit is improved feeding accuracy. Feeding too much results in wasted feed, elevated feed costs, and deteriorating biological FCR, while feeding too little suppresses growth, extends production cycles, and reduces overall performance. The SFR Calculator helps strike the correct balance by recommending feed quantities that match expected growth potential under current conditions.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 2: Operational Consistency */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-2">Better Operational Consistency</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The SFR Calculator also supports better operational consistency. By standardising feeding decisions across cages, sites, and teams, it reduces variability caused by subjective judgement or experience-based feeding adjustments. This improves comparability of performance between cages and strengthens overall production control.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 3: Cost Control and Forecasting */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-2">Enhanced Cost Control and Forecasting</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        From a management perspective, the SFR Calculator enhances cost control and forecasting. Feed represents the largest variable cost in aquaculture, and even small daily inefficiencies compound over time. Accurate SFR-based feeding improves predictability of feed usage, supports budgeting, and reduces unexpected deviations in FCR.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Conclusion: Data-Driven Management */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[#0891b2]" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-[#0891b2] mb-2">Data-Driven Feed Management</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Ultimately, the SFR Calculator transforms feeding from a reactive task into a data-driven management tool—supporting optimal growth, protecting margins, and ensuring that feed is converted into biomass as efficiently as possible.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="SFR Calculator"
              youtubeUrl={HELP_URLS.sfrCalculator.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/sfr-calculator')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-sfr-calculator-btn"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Open SFR Calculator
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
